/**
 * Boutons et petits éléments d'interface partagés : une seule couleur d'accent (zce-blue),
 * fond blanc et bordure fine pour le reste, icônes en trait, pas d'emoji.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type BtnVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'onDark' | 'primaryOnDark';
export type BtnSize = 'sm' | 'md';

const VARIANT: Record<BtnVariant, string> = {
  primary: 'bg-zce-blue text-white shadow-sm hover:bg-zce-blue-dark',
  secondary: 'bg-white text-gray-700 border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-gray-300',
  danger: 'bg-white text-red-600 border border-gray-200 shadow-sm hover:bg-red-50 hover:border-red-200',
  ghost: 'text-gray-600 hover:bg-gray-100',
  // sur un bandeau sombre
  onDark: 'bg-white/10 text-white border border-white/20 backdrop-blur hover:bg-white/15',
  primaryOnDark: 'bg-white text-zce-navy shadow-sm hover:bg-blue-50',
};
// état « enfoncé » d'un bouton bascule (aria-pressed)
const ACTIVE: Partial<Record<BtnVariant, string>> = {
  secondary: 'bg-blue-50 text-zce-blue border-blue-200 hover:bg-blue-50 hover:border-blue-300',
  ghost: 'bg-blue-50 text-zce-blue',
  onDark: 'bg-white text-zce-navy border-white hover:bg-white',
};
const SIZE: Record<BtnSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
};

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: BtnSize;
  /** Icône en trait, placée avant le texte (taille forcée à 16 px). */
  icon?: ReactNode;
  /** Bouton bascule enfoncé. */
  active?: boolean;
  children?: ReactNode;
}

export function Btn({ variant = 'secondary', size = 'md', icon, active, className = '', children, type = 'button', ...rest }: BtnProps) {
  return (
    <button
      type={type}
      aria-pressed={active === undefined ? undefined : active}
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-medium whitespace-nowrap transition-colors touch-manipulation
        focus:outline-none focus-visible:ring-2 focus-visible:ring-zce-blue/40 disabled:opacity-50 disabled:cursor-not-allowed
        [&>svg]:w-4 [&>svg]:h-4 [&>svg]:shrink-0
        ${SIZE[size]} ${active ? ACTIVE[variant] ?? VARIANT[variant] : VARIANT[variant]} ${className}`}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

export type Tone = 'blue' | 'amber' | 'emerald' | 'violet' | 'rose' | 'cyan' | 'slate';
const TONE: Record<Tone, string> = {
  blue: 'bg-blue-50 text-zce-blue',
  amber: 'bg-amber-50 text-amber-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  violet: 'bg-violet-50 text-violet-600',
  rose: 'bg-rose-50 text-rose-600',
  cyan: 'bg-cyan-50 text-cyan-600',
  slate: 'bg-slate-100 text-slate-600',
};

/** Pastille carrée teintée qui porte une icône (cartes de statistiques, en-têtes de section). */
export function IconTile({ tone = 'blue', size = 'md', className = '', children }: { tone?: Tone; size?: 'sm' | 'md'; className?: string; children: ReactNode }) {
  const dim = size === 'sm' ? 'w-9 h-9 rounded-lg [&>svg]:w-4 [&>svg]:h-4' : 'w-11 h-11 rounded-xl [&>svg]:w-5 [&>svg]:h-5';
  return <span className={`inline-flex items-center justify-center shrink-0 ${dim} ${TONE[tone]} ${className}`}>{children}</span>;
}

const DOT: Record<Tone, string> = {
  blue: 'bg-blue-500', amber: 'bg-amber-400', emerald: 'bg-emerald-500', violet: 'bg-violet-500', rose: 'bg-pink-500', cyan: 'bg-cyan-500', slate: 'bg-slate-400',
};

/** Point de couleur (légendes, boutons d'ajout) : la couleur identifie le type d'élément sans teinter tout le bouton. */
export function Dot({ tone, className = '' }: { tone: Tone; className?: string }) {
  return <span aria-hidden className={`inline-block w-2 h-2 rounded-full shrink-0 ${DOT[tone]} ${className}`} />;
}

/** Étiquette de légende : fond neutre, point de couleur, texte. */
export function LegendChip({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-50 border border-gray-200 text-xs font-medium text-gray-600">
      <Dot tone={tone} />
      {children}
    </span>
  );
}
